import React, { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertTriangle,
  Clock,
  CheckCircle,
  CalendarDays,
  ExternalLink,
  Flame,
  Sparkles,
  BookOpen,
  Zap,
} from "lucide-react";

/* ─────────────────────────────────────────────
   DESIGN TOKENS — matches Dashboard.jsx palette
───────────────────────────────────────────── */
const T = {
  bg:           "#0f1117",
  surface:      "#161b27",
  surfaceHover: "#1c2333",
  raised:       "#1e2538",
  border:       "rgba(255,255,255,0.07)",
  borderStrong: "rgba(255,255,255,0.13)",
  text:         "#e8eaf0",
  textMuted:    "#6b7280",
  textDim:      "#9ca3af",
  accent:       "#6366f1",
  accentLight:  "rgba(99,102,241,0.15)",
  accentGlow:   "rgba(99,102,241,0.25)",
  success:      "#10b981",
  successLight: "rgba(16,185,129,0.12)",
  warning:      "#f59e0b",
  warningLight: "rgba(245,158,11,0.12)",
  danger:       "#ef4444",
  dangerLight:  "rgba(239,68,68,0.12)",
  orange:       "#f97316",
  orangeLight:  "rgba(249,115,22,0.12)",
  radius:       "12px",
  radiusSm:     "8px",
  radiusXs:     "5px",
};

/* ─────────────────────────────────────────────
   INJECTED STYLES
───────────────────────────────────────────── */
const INJECTED_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800&display=swap');

  .dq-sections * { box-sizing: border-box; }

  /* ── Celebrate empty ── */
  .empty-celebrate {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 24px;
    text-align: center;
    gap: 16px;
  }
  .empty-graphic { position: relative; display: inline-flex; align-items: center; justify-content: center; }
  .empty-badge { width: 72px; height: 72px; filter: drop-shadow(0 0 16px rgba(99,102,241,0.4)); }
  .confetti {
    position: absolute;
    width: 6px; height: 6px;
    border-radius: 50%;
    animation: confetti-float 2.4s ease-in-out infinite;
  }
  .c1 { background: #6366f1; top: -12px; left: 0;   animation-delay: 0s;    animation-duration: 2.1s; }
  .c2 { background: #10b981; top: -8px;  right: -4px; animation-delay: 0.3s; animation-duration: 2.5s; }
  .c3 { background: #f59e0b; bottom: -8px; left: -8px; animation-delay: 0.6s; animation-duration: 2.2s; }
  .c4 { background: #f97316; top: 4px;   right: -16px; animation-delay: 0.9s; animation-duration: 2.8s; }
  .c5 { background: #a78bfa; bottom: -4px; right: 8px;  animation-delay: 1.2s; animation-duration: 2.3s; }
  @keyframes confetti-float {
    0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.8; }
    50%       { transform: translateY(-10px) rotate(180deg); opacity: 1; }
  }
  .empty-copy h3 { margin: 0; font-size: 15px; font-weight: 700; color: #e8eaf0; }
  .empty-copy p  { margin: 4px 0 0; font-size: 13px; color: #6b7280; }
  .empty-cta {
    margin-top: 12px;
    background: rgba(99,102,241,0.15);
    border: 1px solid rgba(99,102,241,0.3);
    color: #a5b4fc;
    border-radius: 8px;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    transition: background 0.15s;
  }
  .empty-cta:hover { background: rgba(99,102,241,0.25); }

  /* ── Assignment card ── */
  .dq-asgn-card {
    background: ${T.surface};
    border: 1px solid ${T.border};
    border-radius: ${T.radius};
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    transition: border-color 0.15s, transform 0.15s;
  }
  .dq-asgn-card:hover {
    border-color: ${T.borderStrong};
    transform: translateY(-1px);
  }
  .dq-asgn-card.overdue  { border-left: 3px solid ${T.danger}; }
  .dq-asgn-card.duetoday { border-left: 3px solid ${T.orange}; }
  .dq-asgn-card.upcoming { border-left: 3px solid ${T.accent}; }
  .dq-asgn-card.completed {
    border-left: 3px solid ${T.success};
    opacity: 0.7;
  }

  /* ── Priority item ── */
  .dq-priority-item {
    background: ${T.surface};
    border: 1px solid ${T.border};
    border-radius: ${T.radius};
    padding: 13px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    transition: border-color 0.15s;
    font-family: 'DM Sans', sans-serif;
  }
  .dq-priority-item:hover { border-color: ${T.borderStrong}; }
  .dq-priority-item.is-overdue { border-left: 3px solid ${T.danger}; }
  .dq-priority-item.is-today   { border-left: 3px solid ${T.orange}; }

  /* ── Buttons ── */
  .dq-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border-radius: 7px;
    font-size: 12px;
    font-weight: 600;
    padding: 5px 11px;
    cursor: pointer;
    border: 1px solid transparent;
    font-family: 'DM Sans', sans-serif;
    white-space: nowrap;
    transition: all 0.15s;
    text-decoration: none;
  }
  .dq-btn-view {
    background: rgba(255,255,255,0.05);
    border-color: ${T.border};
    color: ${T.textDim};
  }
  .dq-btn-view:hover { background: rgba(255,255,255,0.09); color: ${T.text}; }
  .dq-btn-snooze {
    background: ${T.warningLight};
    border-color: rgba(245,158,11,0.2);
    color: #fbbf24;
  }
  .dq-btn-snooze:hover { background: rgba(245,158,11,0.2); }
  .dq-btn-done {
    background: ${T.successLight};
    border-color: rgba(16,185,129,0.2);
    color: #34d399;
  }
  .dq-btn-done:hover { background: rgba(16,185,129,0.22); }
  .dq-btn-done:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* ── Section headings ── */
  .dq-section-heading {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: ${T.textMuted};
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 1px solid ${T.border};
  }
  .dq-section-heading .count-badge {
    margin-left: auto;
    background: ${T.accentLight};
    color: #a5b4fc;
    border-radius: 5px;
    padding: 1px 7px;
    font-size: 11px;
    font-weight: 700;
  }
  .dq-section-heading .count-badge.danger {
    background: ${T.dangerLight};
    color: #f87171;
  }
  .dq-section-heading .count-badge.warning {
    background: ${T.warningLight};
    color: #fbbf24;
  }
  .dq-section-heading .count-badge.success {
    background: ${T.successLight};
    color: #34d399;
  }

  /* ── AI tip item ── */
  .dq-ai-tip {
    background: ${T.bg};
    border: 1px solid ${T.border};
    border-radius: ${T.radiusSm};
    padding: 11px 13px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    transition: border-color 0.15s;
  }
  .dq-ai-tip:hover { border-color: rgba(99,102,241,0.3); }
  .dq-ai-tip-title { font-size: 13px; font-weight: 600; color: ${T.text}; line-height: 1.3; }
  .dq-ai-tip-body  { font-size: 12px; color: ${T.textMuted}; line-height: 1.5; }

  /* ── Progress bar ── */
  .dq-prog-track {
    height: 5px;
    background: ${T.border};
    border-radius: 99px;
    overflow: hidden;
    margin: 6px 0;
  }
  .dq-prog-fill {
    height: 100%;
    border-radius: 99px;
    transition: width 0.6s ease;
  }

  /* ── Empty feed message ── */
  .dq-feed-empty {
    border: 1px dashed rgba(255,255,255,0.08);
    border-radius: ${T.radius};
    padding: 18px;
    font-size: 13px;
    color: ${T.textMuted};
    text-align: center;
    background: rgba(255,255,255,0.015);
    margin-bottom: 12px;
  }

  /* ── Urgency pill ── */
  .dq-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border-radius: 5px;
    padding: 2px 7px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  /* ── Mobile responsive ── */

  /* Priority item: stack vertically on mobile */
  @media (max-width: 768px) {
    .dq-priority-item {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 10px !important;
      padding: 12px 14px !important;
    }
    .dq-priority-actions {
      display: flex !important;
      flex-wrap: wrap !important;
      gap: 6px !important;
      width: 100% !important;
    }
    .dq-priority-actions .dq-btn {
      flex: 1 1 auto !important;
      justify-content: center !important;
      min-width: 0 !important;
      font-size: 11px !important;
      padding: 6px 10px !important;
    }
    .dq-priority-meta {
      flex-wrap: wrap !important;
      gap: 4px 8px !important;
    }
    /* Assignment card grid: single column */
    .dq-sections .assignment-grid,
    .dq-sections [style*="gridTemplateColumns"] {
      grid-template-columns: 1fr !important;
    }
    /* Progress row: stack */
    .dq-progress-row-grid {
      grid-template-columns: 1fr !important;
    }
  }

  @media (max-width: 480px) {
    .dq-priority-item {
      padding: 10px 12px !important;
    }
    .dq-priority-actions .dq-btn {
      font-size: 11px !important;
      padding: 7px 8px !important;
    }
    .dq-asgn-card {
      padding: 12px 13px !important;
    }
    .dq-feed-empty {
      padding: 14px !important;
      font-size: 12px !important;
    }
  }

  /* Prevent all horizontal overflow */
  .dq-sections {
    max-width: 100%;
    overflow-x: hidden;
  }
  .dq-priority-item,
  .dq-asgn-card {
    min-width: 0;
    max-width: 100%;
    overflow-wrap: break-word;
    word-break: break-word;
  }
`;

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const fmt = (d) => new Date(d).toLocaleString("en-US", {
  month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
});
const isToday = (d) => new Date(d).toDateString() === new Date().toDateString();

function estTimeFor(a) {
  const name = `${a.name || a.title}`.toLowerCase();
  if (name.includes("quiz") || name.includes("test")) return "~20 min";
  if (name.includes("essay") || name.includes("paper") || name.includes("project")) return "~3 hrs";
  return "~60 min";
}

function UrgencyPill({ due, submitted }) {
  if (submitted) return (
    <span className="dq-pill" style={{ background: T.successLight, color: "#34d399" }}>
      <CheckCircle size={9} /> Done
    </span>
  );
  const now = new Date();
  const d = new Date(due);
  const hrs = (d - now) / 3600000;
  if (hrs < 0)  return <span className="dq-pill" style={{ background: T.dangerLight,  color: "#f87171" }}><AlertTriangle size={9} /> Overdue</span>;
  if (hrs < 24) return <span className="dq-pill" style={{ background: T.orangeLight,  color: "#fb923c" }}><Clock size={9} /> Due today</span>;
  if (hrs < 72) return <span className="dq-pill" style={{ background: T.warningLight, color: "#fbbf24" }}><Clock size={9} /> Due soon</span>;
  return <span className="dq-pill" style={{ background: T.accentLight, color: "#a5b4fc" }}><CalendarDays size={9} /> Upcoming</span>;
}

/* ─────────────────────────────────────────────
   CELEBRATE EMPTY
───────────────────────────────────────────── */
function CelebrateEmpty({ title = "Nothing overdue — nice work!", subtitle = "You're all caught up. Keep the streak going 💪", ctaText, onCta }) {
  return (
    <div className="empty-celebrate">
      <div className="empty-graphic" aria-hidden>
        <svg viewBox="0 0 64 64" className="empty-badge">
          <defs>
            <linearGradient id="grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#a78bfa"/>
              <stop offset="100%" stopColor="#6366f1"/>
            </linearGradient>
          </defs>
          <circle cx="32" cy="32" r="28" fill="url(#grad)" opacity="0.15"/>
          <circle cx="32" cy="32" r="20" fill="url(#grad)"/>
          <path d="M24 33.5l5 5 11-11" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span className="confetti c1" /><span className="confetti c2" />
        <span className="confetti c3" /><span className="confetti c4" />
        <span className="confetti c5" />
      </div>
      <div className="empty-copy">
        <h3>{title}</h3>
        <p>{subtitle}</p>
        {ctaText && <button className="empty-cta" onClick={onCta}>{ctaText}</button>}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SNOOZE HELPERS
───────────────────────────────────────────── */
const SNOOZE_KEY = "cs_snoozed";
function loadSnoozed() {
  try { const r = localStorage.getItem(SNOOZE_KEY); return r ? JSON.parse(r) : {}; }
  catch { return {}; }
}
function saveSnoozed(m) { try { localStorage.setItem(SNOOZE_KEY, JSON.stringify(m)); } catch {} }
function isSnoozedUntil(map, id) { const ts = map[id]; return typeof ts === "number" ? ts : 0; }

/* ─────────────────────────────────────────────
   PRIORITY TASKS
───────────────────────────────────────────── */
export function PriorityTasks({ assignments = [], max = 4, onMarkDone }) {
  const [snoozed, setSnoozed] = useState({});

  useEffect(() => {
    const m = loadSnoozed();
    const now = Date.now();
    let changed = false;
    for (const [k, v] of Object.entries(m)) {
      if (typeof v !== "number" || v <= now) { delete m[k]; changed = true; }
    }
    if (changed) saveSnoozed(m);
    setSnoozed(m);
  }, []);

  const overdue = useMemo(() => {
    const now = new Date();
    return assignments.filter((a) => new Date(a.due_at || a.dueAt) < now && !a.isSubmitted);
  }, [assignments]);

  const dueToday = useMemo(() => (
    assignments.filter((a) => isToday(a.due_at || a.dueAt) && !a.isSubmitted)
  ), [assignments]);

  const nowMs = Date.now();
  const visible = [...overdue, ...dueToday].filter((a) => isSnoozedUntil(snoozed, a.id) <= nowMs);
  const list = visible.slice(0, max);
  const isOverdue = (a) => new Date(a.due_at || a.dueAt) < new Date();

  function snoozeItem(a, hours = 24) {
    const until = Date.now() + hours * 3600 * 1000;
    const next = { ...snoozed, [a.id]: until };
    setSnoozed(next);
    saveSnoozed(next);
  }

  function clearAllSnoozed() {
  setSnoozed({});
  saveSnoozed({});
  }

  return (
    <div className="dq-sections" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{INJECTED_CSS}</style>

      {/* Unsnooze all — shown subtly so users can recover snoozed items */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button
          onClick={clearAllSnoozed}
          className="dq-btn dq-btn-view"
          style={{ fontSize: 11 }}
        >
          Unsnooze all
        </button>
      </div>

      {list.length === 0 ? (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "14px 16px",
          background: T.successLight,
          border: `1px solid rgba(16,185,129,0.2)`,
          borderRadius: T.radius,
          color: "#34d399",
          fontSize: 13,
          fontWeight: 600,
        }}>
          <CheckCircle size={16} />
          You're all caught up for today — great work! 🎉
        </div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          <AnimatePresence>
            {list.map((a, i) => (
              <motion.div
                key={a.id}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0, transition: { delay: i * 0.05 } }}
                exit={{ opacity: 0, x: -20 }}
                className={`dq-priority-item ${isOverdue(a) ? "is-overdue" : "is-today"}`}
              >
                {/* Left: icon + text */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flex: 1, minWidth: 0 }}>
                  <div style={{
                    width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                    background: isOverdue(a) ? T.dangerLight : T.orangeLight,
                    border: `1px solid ${isOverdue(a) ? "rgba(239,68,68,0.2)" : "rgba(249,115,22,0.2)"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {isOverdue(a)
                      ? <AlertTriangle size={15} color="#f87171" />
                      : <Clock size={15} color="#fb923c" />
                    }
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: T.text, lineHeight: 1.3, marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                      {a.name || a.title}
                    </div>
                    <div className="dq-priority-meta" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, color: T.textMuted }}>{a.courseName || a.course}</span>
                      <span style={{ width: 2, height: 2, borderRadius: "50%", background: T.textMuted, flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: T.textMuted }}>{fmt(a.due_at || a.dueAt)}</span>
                      <span style={{ width: 2, height: 2, borderRadius: "50%", background: T.textMuted, flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: T.textMuted }}>{estTimeFor(a)}</span>
                    </div>
                  </div>
                </div>

                {/* Right: actions */}
                <div className="dq-priority-actions" style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  {a.html_url && (
                    <a href={a.html_url} target="_blank" rel="noreferrer" className="dq-btn dq-btn-view">
                      <ExternalLink size={11} /> Open
                    </a>
                  )}
                  <button onClick={() => snoozeItem(a, 24)} className="dq-btn dq-btn-snooze" title="Snooze 24h">
                    💤 Snooze
                  </button>
                  {onMarkDone && (
                    <button
                      onClick={() => onMarkDone(a)}
                      className="dq-btn dq-btn-done"
                      disabled={!!a.isSubmitted}
                      title={a.isSubmitted ? "Already completed" : "Mark as done"}
                    >
                      <CheckCircle size={11} /> Done
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   PROGRESS ROW
───────────────────────────────────────────── */
export function ProgressRow({ weekly = { done: 0, total: 0 }, streakDays = 0 }) {
  const pct = weekly.total ? Math.round((weekly.done / weekly.total) * 100) : 0;

  return (
    <div className="dq-sections dq-progress-row-grid" style={{
      display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12,
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <style>{INJECTED_CSS}</style>

      {/* Weekly progress */}
      <div style={{
        background: T.bg,
        border: `1px solid ${T.border}`,
        borderRadius: T.radius,
        padding: "14px 16px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: T.accentLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CalendarDays size={13} color="#a5b4fc" />
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            This Week
          </span>
          <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700, color: T.text }}>
            {weekly.done}<span style={{ color: T.textMuted, fontWeight: 400 }}>/{weekly.total}</span>
          </span>
        </div>
        <div className="dq-prog-track">
          <div className="dq-prog-fill" style={{ width: `${pct}%`, background: "linear-gradient(90deg, #6366f1, #818cf8)" }} />
        </div>
        <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>
          {pct}% of weekly assignments completed
        </div>
      </div>

      {/* Streak */}
      <div style={{
        background: T.bg,
        border: `1px solid ${T.border}`,
        borderRadius: T.radius,
        padding: "14px 16px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: "rgba(249,115,22,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Flame size={13} color="#fb923c" />
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Streak
          </span>
          <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700, color: "#fb923c" }}>
            {streakDays} day{streakDays !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="dq-prog-track">
          <div className="dq-prog-fill" style={{
            width: `${Math.min(streakDays * 10, 100)}%`,
            background: "linear-gradient(90deg, #f97316, #fbbf24)",
          }} />
        </div>
        <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>
          {streakDays === 0 ? "Submit something to start your streak" : streakDays < 5 ? "Building momentum — keep going! 💪" : "On fire — don't break it! 🔥"}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ASSIGNMENT CARD
───────────────────────────────────────────── */
function AssignmentCard({ a }) {
  const due = a.due_at || a.dueAt;
  const submitted = !!a.isSubmitted;
  const now = new Date();
  const dueDate = new Date(due);
  const hrs = (dueDate - now) / 3600000;

  let cardClass = "dq-asgn-card upcoming";
  if (submitted) cardClass = "dq-asgn-card completed";
  else if (hrs < 0) cardClass = "dq-asgn-card overdue";
  else if (isToday(due)) cardClass = "dq-asgn-card duetoday";

  return (
    <div className={cardClass}>
      {/* Top row: title + urgency pill */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: T.text, lineHeight: 1.35, flex: 1 }}>
          {a.name || a.title}
        </div>
        <UrgencyPill due={due} submitted={submitted} />
      </div>

      {/* Meta row */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px" }}>
        <span style={{ fontSize: 11, color: T.textMuted, display: "flex", alignItems: "center", gap: 4 }}>
          <BookOpen size={10} /> {a.courseName || a.course}
        </span>
        <span style={{ fontSize: 11, color: T.textMuted, display: "flex", alignItems: "center", gap: 4 }}>
          <Clock size={10} /> {fmt(due)}
        </span>
        <span style={{ fontSize: 11, color: T.textMuted, display: "flex", alignItems: "center", gap: 4 }}>
          <Zap size={10} /> {estTimeFor(a)}
        </span>
      </div>

      {/* Footer: link */}
      {a.html_url && (
        <div style={{ paddingTop: 2 }}>
          <a
            href={a.html_url}
            target="_blank"
            rel="noopener noreferrer"
            className="dq-btn dq-btn-view"
            style={{ fontSize: 11 }}
          >
            <ExternalLink size={10} /> View in Canvas
          </a>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ASSIGNMENT FEED
───────────────────────────────────────────── */
export function AssignmentFeed({
  dueToday = [],
  overdue = [],
  upcoming = [],
  completed = [],
  renderCard,
  activeView = "upcoming",
  dateFilter = null,
}) {
  const sameDay = (a, d) => new Date(a.due_at || a.dueAt).toDateString() === d.toDateString();
  const byDate = (list) => (dateFilter ? list.filter((a) => sameDay(a, dateFilter)) : list);

  const DUE_TODAY = byDate(dueToday);
  const OVERDUE   = byDate(overdue);
  const UPCOMING  = byDate(upcoming);
  const COMPLETED = byDate(completed);

  function Section({ title, icon, items, emptyMsg, badgeVariant }) {
    if (!items.length) {
      return <div className="dq-feed-empty">{emptyMsg}</div>;
    }
    return (
      <div style={{ marginBottom: 20 }}>
        <div className="dq-section-heading">
          {icon}
          {title}
          <span className={`count-badge ${badgeVariant || ""}`}>{items.length}</span>
        </div>
        <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fill, minmax(min(260px, 100%), 1fr))", maxWidth: "100%", overflow: "hidden" }}>
          {items.map((a) => (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              {renderCard ? renderCard(a) : <AssignmentCard a={a} />}
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  if (activeView === "overdue") {
    if (OVERDUE.length === 0) {
      return (
        <div className="dq-sections">
          <style>{INJECTED_CSS}</style>
          <CelebrateEmpty
            title="Nothing overdue — nice work!"
            subtitle="You're on top of everything. Maybe review upcoming tasks or take a short break 🎧"
            ctaText="Plan next session"
            onCta={() => { const el = document.querySelector(".dh-search input"); if (el) el.focus(); }}
          />
        </div>
      );
    }
    return (
      <div className="dq-sections" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <style>{INJECTED_CSS}</style>
        <Section title="Overdue" icon={<AlertTriangle size={12} />} items={OVERDUE} emptyMsg="" badgeVariant="danger" />
      </div>
    );
  }

  if (activeView === "completed") {
    return (
      <div className="dq-sections" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <style>{INJECTED_CSS}</style>
        <Section title="Completed" icon={<CheckCircle size={12} />} items={COMPLETED} emptyMsg="No completed items yet." badgeVariant="success" />
      </div>
    );
  }

  return (
    <div className="dq-sections" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{INJECTED_CSS}</style>
      <Section title="Due Today" icon={<Clock size={12} />} items={DUE_TODAY} emptyMsg="Nothing due today." badgeVariant="warning" />
      {OVERDUE.length > 0 && (
        <Section title="Overdue" icon={<AlertTriangle size={12} />} items={OVERDUE} emptyMsg="" badgeVariant="danger" />
      )}
      <Section title="Upcoming" icon={<CalendarDays size={12} />} items={UPCOMING} emptyMsg="Nothing coming up in the next 7 days." />
    </div>
  );
}

/* ─────────────────────────────────────────────
   AI HELPER
───────────────────────────────────────────── */
export function AIHelper({ assignments = [] }) {
  const now = new Date();
  const overdue = assignments.filter((a) => new Date(a.due_at || a.dueAt) < now && !a.isSubmitted);
  const dueSoon = assignments.filter((a) => {
    const due = new Date(a.due_at || a.dueAt);
    const hrs = (due - now) / 36e5;
    return hrs > 0 && hrs <= 24 && !a.isSubmitted;
  });

  const suggestions = [
    ...overdue.slice(0, 2).map((a) => ({
      icon: "🚨",
      label: "Recover",
      title: `Address "${a.name || a.title}"`,
      tip: `Email your professor about the late policy and submit ASAP. Est. ${estTimeFor(a)}.`,
      accent: T.dangerLight,
      accentBorder: "rgba(239,68,68,0.2)",
      accentLabel: "#f87171",
    })),
    ...dueSoon.slice(0, 3).map((a) => ({
      icon: "⚡",
      label: "Start now",
      title: `"${a.name || a.title}"`,
      tip: `Block 25 min to make a dent. Due ${fmt(a.due_at || a.dueAt)}.`,
      accent: T.orangeLight,
      accentBorder: "rgba(249,115,22,0.2)",
      accentLabel: "#fb923c",
    })),
  ];

  return (
    <div className="dq-sections" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{INJECTED_CSS}</style>

      {suggestions.length === 0 ? (
        <div style={{
          background: T.successLight,
          border: "1px solid rgba(16,185,129,0.2)",
          borderRadius: T.radius,
          padding: "16px",
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
        }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(16,185,129,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Sparkles size={15} color="#34d399" />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#34d399", marginBottom: 3 }}>All clear!</div>
            <div style={{ fontSize: 12, color: T.textMuted }}>No pressing tasks right now. Great job staying ahead of the curve.</div>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {suggestions.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0, transition: { delay: i * 0.07 } }}
              className="dq-ai-tip"
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  background: s.accent, border: `1px solid ${s.accentBorder}`,
                  borderRadius: 4, padding: "1px 6px",
                  fontSize: 10, fontWeight: 700, color: s.accentLabel,
                  textTransform: "uppercase", letterSpacing: "0.05em",
                }}>
                  {s.icon} {s.label}
                </span>
              </div>
              <div className="dq-ai-tip-title">{s.title}</div>
              <div className="dq-ai-tip-body">{s.tip}</div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}